import {
  EolSectionType,
  type EolSectionTypeValue,
  placeOfCareOptions
} from './eolTypes'

export type FieldType = 'textarea' | 'date' | 'select'

export interface EolFieldConfig {
  key: string
  type: FieldType
  label: string
  options?: { value: string; label: string }[]
}

export interface EolSectionConfigItem {
  sectionType: EolSectionTypeValue
  label: string
  description: string
  color: string
  maxSections: number
  maxEntries: number
  fields: EolFieldConfig[]
}

export const eolSectionConfig: EolSectionConfigItem[] = [
  {
    sectionType: EolSectionType.custom,
    label: 'Custom',
    description: 'A custom section for additional information.',
    color: '#6366F1',
    maxSections: Infinity,
    maxEntries: Infinity,
    fields: [{ key: 'description', type: 'textarea', label: 'Description' }]
  },
  {
    sectionType: EolSectionType.anticipatoryActions,
    label: 'Anticipatory actions',
    description: 'Actions planned in anticipation of future care needs.',
    color: '#F59E0B',
    maxSections: Infinity,
    maxEntries: Infinity,
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      { key: 'date', type: 'date', label: 'Date' }
    ]
  },
  {
    sectionType: EolSectionType.anticipatoryMedicinesEquipment,
    label: 'Anticipatory medicines/equipment',
    description: 'Medicines and equipment prepared for anticipated needs.',
    color: '#3B82F6',
    maxSections: Infinity,
    maxEntries: Infinity,
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      { key: 'observationDate', type: 'date', label: 'Date of observation' }
    ]
  },
  {
    sectionType: EolSectionType.prognosis,
    label: 'Prognosis',
    description:
      'Prognosis records including awareness and estimated timelines.',
    color: '#1E40AF',
    maxSections: Infinity,
    maxEntries: Infinity,
    fields: [
      {
        key: 'awarenessOfPrognosis',
        type: 'textarea',
        label: 'Awareness of prognosis'
      },
      { key: 'awarenessDate', type: 'date', label: 'Awareness date' },
      {
        key: 'estimatedPrognosis',
        type: 'textarea',
        label: 'Estimated prognosis'
      },
      { key: 'observationDate', type: 'date', label: 'Date of observation' }
    ]
  },
  {
    sectionType: EolSectionType.instructions,
    label: 'Instructions',
    description: 'Detailed instructions for delivering care.',
    color: '#EC4899',
    maxSections: 1,
    maxEntries: Infinity,
    fields: [
      {
        key: 'plannedActivities',
        type: 'textarea',
        label: 'Planned and scheduled activities'
      },
      {
        key: 'completedActions',
        type: 'textarea',
        label: 'Completed actions'
      },
      {
        key: 'howCareShouldBeProvided',
        type: 'textarea',
        label: 'How care should be provided'
      }
    ]
  },
  {
    sectionType: EolSectionType.planOfContents,
    label: 'Plan of contents',
    description: 'Mandatory overview of this End of Life Care Plan.',
    color: '#06B6D4',
    maxSections: Infinity,
    maxEntries: Infinity,
    fields: [{ key: 'details', type: 'textarea', label: 'Details' }]
  },
  {
    sectionType: EolSectionType.preferredPlaceOfCare,
    label: 'Preferred place of care',
    description: "The person's preferred location to receive care.",
    color: '#22C55E',
    maxSections: 1,
    maxEntries: Infinity,
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      { key: 'date', type: 'date', label: 'Date' },
      {
        key: 'snomedCode',
        type: 'select',
        label: 'Preferred place',
        options: placeOfCareOptions
      }
    ]
  },
  {
    sectionType: EolSectionType.preferredPlaceOfDeath,
    label: 'Preferred place of death',
    description: "The person's preferred location at end of life.",
    color: '#8B5CF6',
    maxSections: 1,
    maxEntries: Infinity,
    fields: [
      { key: 'description', type: 'textarea', label: 'Description' },
      { key: 'date', type: 'date', label: 'Date' },
      {
        key: 'snomedCode',
        type: 'select',
        label: 'Preferred place',
        options: placeOfCareOptions
      }
    ]
  },
  {
    sectionType: EolSectionType.thisIsHowToSupportMe,
    label: 'This is how to support me',
    description: 'Personal preferences on how support should be provided.',
    color: '#F97316',
    maxSections: 1,
    maxEntries: Infinity,
    fields: [{ key: 'description', type: 'textarea', label: 'Description' }]
  }
]
